/**
 * Workflow Runner
 *
 * Orchestrates the execution of multi-step workflows.
 * Manages DAG validation, topological sorting, and sequential step execution.
 *
 * @packageDocumentation
 */

import type { WorkflowDefinition, WorkflowStep } from '../types/workflow';
import type { WorkflowRunResult, RunOptions, StepExecutionLog } from '../types/execution';
import type { AdapterExecutor } from '../types/adapter';
import { validateAndSort } from '../validation/dag';
import { ExecutionContextImpl } from '../execution/context';
import { executeStep, validateStepExecution } from '../execution/executor';
import { evaluateCondition } from '../execution/condition-evaluator';

/**
 * Workflow Runner
 *
 * Responsible for orchestrating the complete execution of a workflow.
 * Handles validation, sorting, execution, and result aggregation.
 *
 * @example
 * ```typescript
 * const runner = new WorkflowRunner(adapterExecutor);
 *
 * const result = await runner.run(workflow, { message: 'Hello' });
 *
 * if (result.success) {
 *   console.log('Workflow completed:', result.finalOutput);
 * } else {
 *   console.error('Workflow failed');
 * }
 * ```
 */
export class WorkflowRunner {
  /**
   * Adapter executor function to use for all steps.
   */
  private adapterExecutor: AdapterExecutor;

  /**
   * Creates a new WorkflowRunner instance.
   *
   * @param adapterExecutor - Function that executes adapter calls
   */
  constructor(adapterExecutor: AdapterExecutor) {
    this.adapterExecutor = adapterExecutor;
  }

  /**
   * Executes a complete workflow from start to finish.
   *
   * This method:
   * 1. Validates the workflow DAG structure
   * 2. Topologically sorts steps based on dependencies
   * 3. Initializes execution context
   * 4. Executes each step sequentially in dependency order
   * 5. Handles step failures (aborts on first failure)
   * 6. Aggregates results into a WorkflowRunResult
   *
   * @param workflow - The workflow definition to execute
   * @param initialInput - Initial input to provide to the workflow
   * @param options - Optional runtime configuration
   * @returns Complete workflow execution result with all step logs
   *
   * @example
   * ```typescript
   * const workflow: WorkflowDefinition = {
   *   workflowName: 'greeting',
   *   steps: [
   *     { stepName: 'greet', model: 'gpt-4o', prompt: 'Say hello' }
   *   ]
   * };
   *
   * const result = await runner.run(workflow, {});
   * console.log(result.finalOutput);
   * ```
   */
  async run(
    workflow: WorkflowDefinition,
    initialInput: any,
    options: RunOptions = {}
  ): Promise<WorkflowRunResult> {
    // Step 1: Validate workflow DAG and get topologically sorted steps
    let sortedSteps: WorkflowStep[];
    try {
      sortedSteps = validateAndSort(workflow);
    } catch (error) {
      // If validation fails, return a failed result immediately
      const runId = this.generateRunId();
      const startedAt = new Date().toISOString();
      const finishedAt = new Date().toISOString();

      return {
        runId,
        workflowName: workflow.workflowName,
        startedAt,
        finishedAt,
        success: false,
        steps: [],
        // Store validation error information
      };
    }

    // Step 2: Initialize execution context
    const context = new ExecutionContextImpl(workflow, initialInput, options);

    // Step 3: Execute steps with parallelization support
    // Group steps by execution level (steps with same dependencies can run in parallel)
    const executionLevels = this.groupStepsByLevel(sortedSteps);
    let aborted = false;

    for (const levelSteps of executionLevels) {
      if (aborted) break;

      // Execute all steps in this level in parallel
      const stepPromises: Promise<{ step: WorkflowStep; log: StepExecutionLog; skipped: boolean }>[] = [];

      for (const step of levelSteps) {
        stepPromises.push(this.executeStepWithCondition(step, context));
      }

      // Wait for all steps in this level to complete
      const results = await Promise.all(stepPromises);

      // Process results
      for (const { step, log, skipped } of results) {
        if (skipped) {
          // Step was skipped due to condition - mark as skipped in logs
          context.appendLog(log);
          // Set a placeholder output so dependent steps can check
          context.setOutput(step.stepName, undefined);
          continue;
        }

        // Store the log
        context.appendLog(log);

        // If step succeeded, store its output
        if (log.success) {
          context.setOutput(step.stepName, log.output);
        } else {
          // Step failed - abort workflow execution
          aborted = true;
          break;
        }
      }
    }

    // Step 4: Finalize context (sets endTime)
    context.finalize();

    // Step 5: Build and return result
    const result: WorkflowRunResult = {
      runId: context.runId,
      workflowName: context.workflowName,
      startedAt: context.startTime,
      finishedAt: context.endTime!,
      success: this.isWorkflowSuccessful(context),
      steps: context.logs,
      finalOutput: this.getFinalOutput(sortedSteps, context),
      reliability: this.computeReliabilitySummary(context.logs),
    };

    return result;
  }

  /**
   * Computes reliability summary from step execution logs.
   * Tracks clean successes, recovered steps, and failures.
   *
   * @param logs - Step execution logs
   * @returns Reliability summary
   */
  private computeReliabilitySummary(logs: StepExecutionLog[]): WorkflowRunResult['reliability'] {
    let cleanSuccesses = 0;
    let recovered = 0;
    let failed = 0;

    for (const log of logs) {
      // Skip steps that were skipped (not executed)
      if (log.skipped) {
        continue;
      }

      if (log.outcome === 'recovered') {
        recovered++;
      } else if (log.outcome === 'failed' || !log.success) {
        failed++;
      } else {
        // outcome === 'success' or undefined with success === true
        cleanSuccesses++;
      }
    }

    const total = cleanSuccesses + recovered + failed;
    const score = total > 0 ? Math.round(((cleanSuccesses + recovered) / total) * 100) : 100;

    return {
      cleanSuccesses,
      recovered,
      failed,
      total,
      score,
      hadRecovery: recovered > 0,
    };
  }

  /**
   * Generates a unique run ID.
   * Uses a simple timestamp-based approach for the MVP.
   *
   * @returns Unique run identifier
   */
  private generateRunId(): string {
    return `run_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Determines if the workflow executed successfully.
   * A workflow is successful if all steps executed and succeeded.
   *
   * @param context - Execution context
   * @returns True if workflow completed successfully
   */
  private isWorkflowSuccessful(context: ExecutionContextImpl): boolean {
    // Workflow is successful if all logs show success
    return context.logs.length > 0 && context.logs.every((log) => log.success);
  }

  /**
   * Gets the final output of the workflow.
   * The final output is the output of the last step in execution order.
   *
   * @param sortedSteps - Steps in topological order
   * @param context - Execution context
   * @returns Output from the final step, or undefined if no steps or last step failed
   */
  private getFinalOutput(
    sortedSteps: WorkflowStep[],
    context: ExecutionContextImpl
  ): any {
    if (sortedSteps.length === 0 || context.logs.length === 0) {
      return undefined;
    }

    // Get the last step that was actually executed (from logs)
    const lastLog = context.logs[context.logs.length - 1];

    // Return its output if it succeeded
    return lastLog.success ? lastLog.output : undefined;
  }

  /**
   * Groups steps by execution level for parallel execution.
   * Steps at the same level have no dependencies on each other and can run in parallel.
   *
   * @param sortedSteps - Topologically sorted steps
   * @returns Array of step arrays, where each inner array can be executed in parallel
   */
  private groupStepsByLevel(sortedSteps: WorkflowStep[]): WorkflowStep[][] {
    if (sortedSteps.length === 0) return [];

    const levels: WorkflowStep[][] = [];
    const stepLevel: Map<string, number> = new Map();

    for (const step of sortedSteps) {
      // Calculate level based on dependencies
      let level = 0;

      if (step.dependsOn && step.dependsOn.length > 0) {
        for (const depName of step.dependsOn) {
          const depLevel = stepLevel.get(depName) ?? 0;
          level = Math.max(level, depLevel + 1);
        }
      }

      stepLevel.set(step.stepName, level);

      // Ensure levels array is large enough
      while (levels.length <= level) {
        levels.push([]);
      }

      levels[level].push(step);
    }

    return levels;
  }

  /**
   * Executes a step with condition evaluation.
   *
   * @param step - Step to execute
   * @param context - Execution context
   * @returns Execution result with step, log, and skipped flag
   */
  private async executeStepWithCondition(
    step: WorkflowStep,
    context: ExecutionContextImpl
  ): Promise<{ step: WorkflowStep; log: StepExecutionLog; skipped: boolean }> {
    // Validate step is ready to execute
    try {
      validateStepExecution(step, context);
    } catch (error) {
      // Validation error means we can't execute this step
      const log: StepExecutionLog = {
        stepName: step.stepName,
        model: step.model,
        startedAt: new Date().toISOString(),
        finishedAt: new Date().toISOString(),
        durationMs: 0,
        success: false,
        error: {
          type: 'ValidationError',
          message: error instanceof Error ? error.message : String(error),
        },
        attempts: 0,
      };

      return { step, log, skipped: false };
    }

    // Evaluate condition if present
    if (step.condition) {
      const conditionResult = evaluateCondition(step.condition, context);

      if (conditionResult.error) {
        // Condition evaluation failed
        const log: StepExecutionLog = {
          stepName: step.stepName,
          model: step.model,
          startedAt: new Date().toISOString(),
          finishedAt: new Date().toISOString(),
          durationMs: 0,
          success: false,
          error: {
            type: 'ConditionError',
            message: conditionResult.error,
          },
          attempts: 0,
        };

        return { step, log, skipped: false };
      }

      if (!conditionResult.shouldExecute) {
        // Condition evaluated to false - skip this step
        const log: StepExecutionLog = {
          stepName: step.stepName,
          model: step.model,
          startedAt: new Date().toISOString(),
          finishedAt: new Date().toISOString(),
          durationMs: 0,
          success: true, // Skipped steps are considered successful
          output: undefined,
          attempts: 0,
          skipped: true,
          skipReason: `Condition "${step.condition}" evaluated to false`,
        };

        return { step, log, skipped: true };
      }
    }

    // Execute the step
    const log = await executeStep(step, context, this.adapterExecutor);

    return { step, log, skipped: false };
  }
}

/**
 * Convenience function to run a workflow without instantiating a runner.
 *
 * @param workflow - Workflow definition
 * @param initialInput - Initial input
 * @param adapterExecutor - Adapter executor function
 * @param options - Runtime options
 * @returns Workflow execution result
 *
 * @example
 * ```typescript
 * const result = await runWorkflow(
 *   workflow,
 *   { message: 'test' },
 *   mockAdapterExecutor
 * );
 * ```
 */
export async function runWorkflow(
  workflow: WorkflowDefinition,
  initialInput: any,
  adapterExecutor: AdapterExecutor,
  options?: RunOptions
): Promise<WorkflowRunResult> {
  const runner = new WorkflowRunner(adapterExecutor);
  return runner.run(workflow, initialInput, options);
}
